<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="560">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="primary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-store-plus</v-icon>
        Alocar / desalocar
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" title="Alocação na loja">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
          Compra física fica no <strong>estoque do grupo</strong>. Aqui só aloca/desaloca claim da
          loja (≤ disponível no grupo).
        </v-alert>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.product_id"
              :items="productItems"
              item-title="title"
              item-value="value"
              label="Produto"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingProducts"
              :disabled="requestingApi || lockProduct"
              density="compact"
              clearable
              hide-details="auto"
              @update:model-value="onProductPicked"
            >
              <template v-slot:no-data>
                <div class="px-4 py-2 text-medium-emphasis">
                  {{ loadingProducts ? 'Carregando…' : 'Nenhum produto disponível' }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col v-if="needsVariant" cols="12">
            <v-select
              v-model="form.product_variant_id"
              :items="variantItems"
              item-title="title"
              item-value="value"
              label="Variação"
              :rules="[(v) => !!v || 'Selecione a variação']"
              :loading="loadingVariants"
              :disabled="requestingApi || lockVariant"
              density="compact"
              clearable
              hide-details="auto"
            >
              <template v-slot:no-data>
                <div class="px-4 py-2 text-medium-emphasis">
                  {{ loadingVariants ? 'Carregando…' : 'Nenhuma variação' }}
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.type"
              :items="typeItems"
              item-title="title"
              item-value="value"
              label="Operação"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.quantity"
              :label="quantityLabel"
              type="number"
              min="0"
              step="1"
              :rules="[
                (v) =>
                  (v !== null && v !== undefined && v !== '' && Number(v) >= 0) ||
                  'Informe quantidade ≥ 0',
              ]"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col v-if="form.type === 'allocate'" cols="12">
            <v-switch
              color="primary"
              label="Quantidade absoluta (definir claim da loja)"
              v-model="form.absolute"
              :disabled="requestingApi"
              hide-details
            ></v-switch>
          </v-col>
          <v-col v-if="form.type === 'allocate' && form.absolute" cols="12" md="6">
            <v-text-field
              v-model="form.min_quantity"
              label="Estoque mínimo"
              type="number"
              min="0"
              step="1"
              clearable
              density="compact"
              :disabled="requestingApi"
              hint="Opcional"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.note"
              label="Observação"
              density="compact"
              :disabled="requestingApi"
              clearable
              hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="dialog = false">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="requestingApi"
          :disabled="requestingApi"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

function emptyForm(storeId) {
  return {
    store_id: storeId != null ? Number(storeId) : null,
    product_id: null,
    product_variant_id: null,
    type: 'allocate',
    quantity: null,
    absolute: true,
    note: null,
    min_quantity: null,
  }
}

export default {
  props: {
    user: Object,
    storeId: {
      type: [Number, String],
      required: true,
    },
    /** Optional prefill: { product_id, product_variant_id?, product?, min_quantity? } */
    modelValue: Object,
    /** Prefer these for product picker (balances / store products) */
    productOptions: {
      type: Array,
      default: () => [],
    },
    floatRight: Boolean,
    hideButton: Boolean,
  },
  emits: ['update:modelValue', 'moved'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingProducts: false,
      loadingVariants: false,
      catalogProducts: [],
      variants: [],
      form: emptyForm(this.storeId),
      typeItems: [
        { title: 'Alocar (do grupo)', value: 'allocate' },
        { title: 'Desalocar (devolver)', value: 'deallocate' },
      ],
    }
  },
  computed: {
    quantityLabel() {
      if (this.form?.type === 'deallocate') return 'Quantidade a desalocar'
      return this.form?.absolute ? 'Claim da loja (absoluto)' : 'Quantidade a adicionar'
    },
    lockProduct() {
      return !!(this.modelValue?.product_id || this.modelValue?.product?.id)
    },
    lockVariant() {
      return !!(this.modelValue?.product_variant_id || this.modelValue?.variant?.id)
    },
    needsVariant() {
      // Show picker when product has non-default variants or more than one
      if (this.variants.length > 1) return true
      if (this.variants.length === 1 && !this.variants[0].is_default) return true
      return !!(this.form?.product_variant_id && this.variants.length)
    },
    variantItems() {
      return (this.variants || []).map((v) => ({
        title: this.variantTitle(v),
        value: v.id,
        active: v.active !== false,
      })).filter((v) => v.active !== false)
    },
    productItems() {
      const map = new Map()
      const add = (id, name, product) => {
        if (id == null || map.has(id)) return
        map.set(id, {
          title: name || `Produto #${id}`,
          value: id,
          product: product || null,
        })
      }
      for (const opt of this.productOptions || []) {
        if (opt == null) continue
        // balance row
        if (opt.product_id != null || opt.product) {
          add(
            opt.product_id || opt.product?.id,
            opt.product?.name || opt.product_name,
            opt.product
          )
          continue
        }
        // store_product row
        if (opt.product) {
          add(opt.product.id || opt.product_id, opt.product.name, opt.product)
          continue
        }
        // plain product
        if (opt.id != null) {
          add(opt.id, opt.name, opt)
        }
      }
      for (const p of this.catalogProducts || []) {
        add(p.id, p.name, p)
      }
      const current = this.form?.product || this.modelValue?.product
      if (current?.id) {
        add(current.id, current.name, current)
      }
      return Array.from(map.values()).sort((a, b) =>
        String(a.title).localeCompare(String(b.title), 'pt-BR')
      )
    },
  },
  methods: {
    variantTitle(v) {
      if (!v) return '—'
      if (v.name_suffix) return v.name_suffix
      if (v.is_default) return 'Padrão'
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return `Variação #${v.id}`
    },
    resetForm() {
      this.form = emptyForm(this.storeId)
      this.variants = []
    },
    hydrateFromModel(model) {
      if (!model || (!model.product_id && !model.product?.id && !model.product_variant_id)) {
        this.resetForm()
        return
      }
      this.form = {
        store_id: Number(this.storeId),
        product_id: model.product_id || model.product?.id || null,
        product_variant_id:
          model.product_variant_id || model.variant?.id || null,
        type:
          model.type === 'deallocate' || model.type === 'out'
            ? 'deallocate'
            : 'allocate',
        quantity: model.quantity != null ? model.quantity : null,
        absolute: model.absolute !== false,
        note: model.note || null,
        min_quantity:
          model.min_quantity === undefined || model.min_quantity === null
            ? null
            : model.min_quantity,
        product: model.product || null,
      }
      this.loadVariantsForProduct(this.form.product_id)
    },
    onProductPicked() {
      this.form.product_variant_id = null
      this.loadVariantsForProduct(this.form.product_id)
    },
    loadVariantsForProduct(productId) {
      const vm = this
      vm.variants = []
      if (!productId) return Promise.resolve()
      vm.loadingVariants = true
      return api.productVariant
        .list({ product_id: Number(productId), active: true })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.variants = Array.isArray(result)
            ? result
            : Array.isArray(items)
              ? items
              : []
          // auto-select sole default
          if (
            !vm.form.product_variant_id &&
            vm.variants.length === 1 &&
            vm.variants[0].is_default
          ) {
            vm.form.product_variant_id = vm.variants[0].id
          }
        })
        .catch(() => {
          vm.variants = []
        })
        .finally(() => {
          vm.loadingVariants = false
        })
    },
    loadCatalogProducts() {
      const vm = this
      if (vm.loadingProducts) return Promise.resolve()
      // skip network if parent already provided options
      if ((vm.productOptions || []).length > 0) {
        return Promise.resolve()
      }
      vm.loadingProducts = true
      return api.storeProduct
        .list({ store_id: Number(vm.storeId), itemsPerPage: 200 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.catalogProducts = (items || [])
            .map((sp) => sp.product)
            .filter((p) => p && p.id)
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.loadingProducts = false
        })
    },
    buildPayload() {
      const f = this.form || {}
      const payload = {
        store_id: Number(this.storeId),
        quantity: Number(f.quantity),
      }
      if (f.product_variant_id != null && f.product_variant_id !== '') {
        payload.product_variant_id = Number(f.product_variant_id)
      }
      if (f.product_id != null && f.product_id !== '') {
        payload.product_id = Number(f.product_id)
      }
      if (f.type === 'allocate') {
        payload.absolute = !!f.absolute
      }
      if (f.note) {
        payload.note = String(f.note)
      }
      return payload
    },
    submit() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.form?.product_id && !vm.form?.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione um produto' })
        return
      }
      if (vm.needsVariant && !vm.form.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a variação' })
        return
      }
      if (!vm.storeId) {
        vm.$notify({ type: 'warn', text: 'Loja inválida' })
        return
      }
      if (
        vm.form.quantity === null ||
        vm.form.quantity === undefined ||
        vm.form.quantity === '' ||
        Number(vm.form.quantity) < 0 ||
        isNaN(Number(vm.form.quantity))
      ) {
        vm.$notify({ type: 'warn', text: 'Informe quantidade ≥ 0' })
        return
      }
      if (vm.form.type === 'deallocate' && !(Number(vm.form.quantity) > 0)) {
        vm.$notify({ type: 'warn', text: 'Informe quantidade a desalocar > 0' })
        return
      }
      if (!vm.form.type) {
        vm.$notify({ type: 'warn', text: 'Selecione a operação' })
        return
      }
      vm.requestingApi = true
      const payload = vm.buildPayload()
      // min_quantity only via legacy move/adjust path — use allocate then optional move adjust
      const req =
        vm.form.type === 'deallocate'
          ? api.stock.deallocate(payload)
          : api.stock.allocate(payload).then((result) => {
              const min = vm.form.min_quantity
              if (
                min === null ||
                min === undefined ||
                min === '' ||
                !vm.form.absolute
              ) {
                return result
              }
              // Set min_quantity via legacy adjust (absolute same qty) — no invent stock
              return api.stock
                .move({
                  store_id: Number(vm.storeId),
                  product_id: payload.product_id,
                  product_variant_id: payload.product_variant_id,
                  type: 'adjust',
                  quantity: Number(payload.quantity),
                  absolute: true,
                  min_quantity: Number(min),
                  note: payload.note || 'min_quantity',
                })
                .catch(() => result)
            })
      return req
        .then((result) => {
          vm.$emit('moved', result)
          vm.$notify({
            type: 'success',
            text:
              vm.form.type === 'deallocate'
                ? 'Estoque desalocado'
                : 'Estoque alocado na loja',
          })
          vm.dialog = false
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (
        newVal &&
        (newVal.product_id || newVal.product?.id || newVal.product_variant_id) &&
        !this.dialog
      ) {
        this.hydrateFromModel(newVal)
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        if (
          this.modelValue &&
          (this.modelValue.product_id ||
            this.modelValue.product?.id ||
            this.modelValue.product_variant_id)
        ) {
          this.hydrateFromModel(this.modelValue)
        } else if (!this.form?.product_id) {
          this.resetForm()
        }
        this.loadCatalogProducts()
      } else {
        this.resetForm()
        this.$emit('update:modelValue', {})
      }
    },
    storeId(newVal) {
      if (this.form) {
        this.form.store_id = newVal != null ? Number(newVal) : null
      }
    },
    'form.type'(newVal) {
      if (newVal !== 'allocate') {
        this.form.absolute = true
      }
    },
  },
}
</script>
